/**
 * Chart settings: a popover rising from the gear in the account bar under the chart (desktop) or a bottom sheet
 * (phones). Every change previews on the chart straight away; Save keeps it, Cancel / Esc / an outside
 * click puts the chart back as it was.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  CHART_LINE_KEYS,
  CHART_LINE_NAMES,
  DEFAULT_CHART_LOOK,
  DEFAULT_CHART_PREFS,
  MAX_TEMPLATE_NAME,
  chartLookFor,
  pairLabel,
  type ChartColor,
  type ChartLine,
  type ChartLineKey,
  type ChartLineStyle,
  type ChartLook,
  type ChartPrefs,
  type ChartTemplate,
} from '@platform/shared';
import { ApiError } from '../lib/api';
import { saveChartPrefs, setChartPreview, setChartSettingsOpen, useChartPrefs, useChartSettingsOpen } from '../stores/chart';
import { cx, Icon, Spinner } from './ui';

type Tab = 'lines' | 'chart' | 'scale' | 'templates';

const SWATCHES: { c: ChartColor; name: string; css: string }[] = [
  { c: 'theme:up', name: 'Green', css: 'var(--green)' },
  { c: 'theme:down', name: 'Red', css: 'var(--red)' },
  { c: 'theme:warn', name: 'Orange', css: 'var(--orange)' },
  { c: 'theme:accent', name: 'Blue', css: 'var(--accent)' },
  { c: '#22b8cf', name: 'Cyan', css: '#22b8cf' },
  { c: '#a970ff', name: 'Purple', css: '#a970ff' },
  { c: '#e8c547', name: 'Yellow', css: '#e8c547' },
  { c: '#ff6fae', name: 'Pink', css: '#ff6fae' },
  { c: 'theme:text', name: 'Text colour', css: 'var(--text)' },
  { c: 'theme:muted', name: 'Grey', css: 'var(--muted)' },
];
const swatchCss = (c: ChartColor) => (c === 'auto' ? 'linear-gradient(135deg, var(--green) 50%, var(--red) 50%)' : (SWATCHES.find((s) => s.c === c)?.css ?? c));
const dashFor = (s: ChartLineStyle, w: number) => (s === 'dashed' ? `${4 + w},${3 + w}` : s === 'dotted' ? `${w},${2 + w * 1.5}` : undefined);

const sectionLabel = 'flex items-baseline justify-between text-[10.5px] font-semibold tracking-[0.07em] text-muted uppercase';

function Switch({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} onClick={() => onChange(!on)} className={cx('relative h-[18px] w-8 shrink-0 rounded-full transition-colors', on ? 'bg-accent' : 'bg-line-strong')}>
      <span className={cx('absolute top-0.5 left-0.5 size-3.5 rounded-full bg-white shadow transition-transform', on && 'translate-x-3.5')} />
    </button>
  );
}

function Seg<T extends string | number>({ value, options, onChange, label, small }: { value: T; options: { v: T; label: ReactNode; title?: string }[]; onChange: (v: T) => void; label: string; small?: boolean }) {
  return (
    <div role="radiogroup" aria-label={label} className="grid auto-cols-fr grid-flow-col rounded-lg bg-raised p-[3px]">
      {options.map((o) => (
        <button
          key={String(o.v)}
          type="button"
          role="radio"
          aria-checked={value === o.v}
          title={o.title}
          onClick={() => onChange(o.v)}
          className={cx('flex items-center justify-center rounded-md font-semibold', small ? 'h-6 text-[11px]' : 'h-7 text-[12px]', value === o.v ? 'bg-bg text-fg shadow-sm' : 'text-muted hover:text-fg')}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Toggle({ title, sub, on, onChange }: { title: string; sub?: string; on: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="flex flex-col">
        <span className="text-[12.5px] font-medium">{title}</span>
        {sub && <span className="text-[11.5px] leading-snug text-muted">{sub}</span>}
      </span>
      <Switch on={on} onChange={onChange} label={title} />
    </div>
  );
}

function Swatches({ value, onChange, label, auto }: { value: ChartColor; onChange: (c: ChartColor) => void; label: string; auto?: boolean }) {
  const custom = value.startsWith('#') && !SWATCHES.some((s) => s.c === value);
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap items-center gap-1">
      {auto && (
        <button type="button" role="radio" aria-checked={value === 'auto'} aria-label="Auto: colour of the last candle" title="Auto: colour of the last candle" onClick={() => onChange('auto')} className={swatchCls(value === 'auto')} style={{ background: swatchCss('auto') }} />
      )}
      {SWATCHES.map((s) => (
        <button key={s.c} type="button" role="radio" aria-checked={value === s.c} aria-label={s.name} title={s.name} onClick={() => onChange(s.c)} className={swatchCls(value === s.c)} style={{ background: s.css }} />
      ))}
      <label title="Custom colour" className={cx('relative grid size-[18px] cursor-pointer place-items-center rounded-full border border-dashed border-line-strong text-[11px] text-muted', custom && 'ring-2 ring-fg ring-offset-1 ring-offset-bg')} style={custom ? { background: value, borderStyle: 'solid' } : undefined}>
        {!custom && '+'}
        <input type="color" aria-label={`${label}: custom colour`} value={value.startsWith('#') ? value : '#f5a524'} onChange={(e) => onChange(e.target.value.toLowerCase() as ChartColor)} className="absolute inset-0 size-full cursor-pointer opacity-0" />
      </label>
    </div>
  );
}
const swatchCls = (on: boolean) => cx('size-[18px] rounded-full border border-line-strong', on && 'ring-2 ring-fg ring-offset-1 ring-offset-bg');

function LineSample({ l }: { l: ChartLine }) {
  return (
    <svg width="34" height="12" viewBox="0 0 34 12" aria-hidden="true" className="shrink-0">
      <line x1="1" x2="33" y1="6" y2="6" stroke={swatchCss(l.color).startsWith('linear') ? 'var(--muted)' : swatchCss(l.color)} strokeWidth={l.width} strokeDasharray={dashFor(l.style, l.width)} strokeLinecap="round" />
    </svg>
  );
}

function LineRow({ k, l, open, onOpen, set }: { k: ChartLineKey; l: ChartLine; open: boolean; onOpen: () => void; set: (patch: Partial<ChartLine>) => void }) {
  const name = CHART_LINE_NAMES[k];
  return (
    <div className={cx('rounded-lg border bg-bg', open ? 'border-line-strong' : 'border-line')}>
      <div className="flex items-center gap-2.5 px-2.5 py-1.5">
        <Switch on={l.show} onChange={(show) => set({ show })} label={`Show ${name}`} />
        <button type="button" onClick={onOpen} aria-expanded={open} className={cx('flex-1 truncate text-left text-[12.5px] font-medium', !l.show && 'text-muted')}>
          {name}
        </button>
        <LineSample l={l} />
        <button type="button" onClick={onOpen} aria-label={`Edit ${name} style`} className={cx('grid size-6 place-items-center rounded-md hover:bg-raised', open ? 'text-accent-fg' : 'text-muted hover:text-fg')}>
          <Icon name="edit" size={12} />
        </button>
      </div>
      {open && (
        <div className="flex flex-col gap-2.5 border-t border-line px-2.5 py-2.5">
          <div className="grid grid-cols-[50px_1fr] items-center gap-2">
            <span className="text-[11.5px] text-muted">Colour</span>
            <Swatches value={l.color} onChange={(color) => set({ color })} label={`${name} colour`} auto={k === 'last'} />
            <span className="text-[11.5px] text-muted">Style</span>
            <Seg
              small
              label={`${name} style`}
              value={l.style}
              onChange={(style) => set({ style })}
              options={(['solid', 'dashed', 'dotted'] as const).map((s) => ({
                v: s,
                title: s[0]!.toUpperCase() + s.slice(1),
                label: (
                  <svg width="22" height="6" viewBox="0 0 22 6" aria-label={s}>
                    <line x1="1" x2="21" y1="3" y2="3" stroke="currentColor" strokeWidth="2" strokeDasharray={dashFor(s, 2)} />
                  </svg>
                ),
              }))}
            />
            <span className="text-[11.5px] text-muted">Width</span>
            <Seg small label={`${name} width`} value={l.width} onChange={(width) => set({ width })} options={([1, 2, 3, 4] as const).map((w) => ({ v: w, label: `${w}px` }))} />
          </div>
          {k !== 'last' && <Toggle title="Label on chart" on={l.label} onChange={(label) => set({ label })} />}
        </div>
      )}
    </div>
  );
}

function Templates({ prefs, look, onApply, onChange }: { prefs: ChartPrefs['templates']; look: ChartLook; onApply: (l: ChartLook) => void; onChange: (t: ChartPrefs['templates']) => void }) {
  const [naming, setNaming] = useState<number | null>(null);
  const [name, setName] = useState('');
  const put = (i: number, t: ChartTemplate | null) => onChange(prefs.map((x, j) => (j === i ? t : x)) as ChartPrefs['templates']);
  return (
    <div className="flex flex-col gap-2">
      <div className={sectionLabel}>
        Saved templates <span className="font-normal tracking-normal normal-case">up to 3</span>
      </div>
      {prefs.map((t, i) => (
        <div key={i} className="flex flex-col gap-2 rounded-lg border border-line bg-bg px-2.5 py-2">
          {naming === i ? (
            <form
              className="flex items-center gap-1.5"
              onSubmit={(e) => {
                e.preventDefault();
                if (!name.trim()) return;
                put(i, { name: name.trim(), look });
                setNaming(null);
              }}
            >
              <input
                autoFocus
                maxLength={MAX_TEMPLATE_NAME}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Template name"
                aria-label={`Template ${i + 1} name`}
                className="h-7 min-w-0 flex-1 rounded-md border border-line bg-panel px-2 text-[12px] outline-none focus:border-accent"
              />
              <button type="submit" disabled={!name.trim()} className="h-7 rounded-md bg-accent px-2.5 text-[11.5px] font-semibold text-white disabled:opacity-50">
                Save
              </button>
              <button type="button" onClick={() => setNaming(null)} className="h-7 rounded-md px-2 text-[11.5px] text-muted hover:text-fg">
                Cancel
              </button>
            </form>
          ) : (
            <div className="flex items-center gap-2">
              <span className={cx('num grid size-5 shrink-0 place-items-center rounded text-[10.5px] font-semibold', t ? 'bg-accent-bg text-accent-fg' : 'bg-raised text-muted')}>{i + 1}</span>
              <span className={cx('flex-1 truncate text-[12.5px]', t ? 'font-medium' : 'text-muted')}>{t ? t.name : 'Empty slot'}</span>
              {t && (
                <button type="button" onClick={() => onApply(t.look)} className="h-6 rounded-md border border-line-strong px-2 text-[11px] font-semibold hover:bg-raised">
                  Apply
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setName(t?.name ?? '');
                  setNaming(i);
                }}
                className="h-6 rounded-md px-1.5 text-[11px] font-semibold text-accent-fg hover:bg-accent-bg"
              >
                {t ? 'Overwrite' : 'Save current'}
              </button>
              {t && (
                <button type="button" onClick={() => put(i, null)} aria-label={`Delete template ${t.name}`} className="grid size-6 place-items-center rounded-md text-muted hover:bg-raised hover:text-down">
                  <Icon name="close" size={11} />
                </button>
              )}
            </div>
          )}
        </div>
      ))}
      <p className="m-0 text-[11.5px] text-muted">A template stores every line, chart and scale setting. Apply one, then Save to use it.</p>
    </div>
  );
}

export function ChartSettings({ symbol, onClose, sheet = false }: { symbol: string; onClose: () => void; sheet?: boolean }) {
  const prefs = useChartPrefs();
  const [look, setLook] = useState<ChartLook>(() => chartLookFor(prefs, symbol));
  const [scope, setScope] = useState<'all' | 'symbol'>(() => (prefs.overrides[symbol] ? 'symbol' : 'all'));
  const [drag, setDrag] = useState(prefs.drag);
  const [confirmDrag, setConfirmDrag] = useState(prefs.confirmDrag);
  const [templates, setTemplates] = useState(prefs.templates);
  const [tab, setTab] = useState<Tab>('lines');
  const [openLine, setOpenLine] = useState<ChartLineKey | null>('tp');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Live preview while open; the chart goes back to the saved look on close.
  useEffect(() => setChartPreview({ symbol, look }), [symbol, look]);
  useEffect(() => () => setChartPreview(null), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const patch = (p: Partial<ChartLook>) => setLook((l) => ({ ...l, ...p }));
  const setLine = (k: ChartLineKey, p: Partial<ChartLine>) => setLook((l) => ({ ...l, lines: { ...l.lines, [k]: { ...l.lines[k], ...p } } }));

  const save = async () => {
    setSaving(true);
    setErr(null);
    const overrides = { ...prefs.overrides };
    let base = prefs.look;
    if (scope === 'symbol') overrides[symbol] = look;
    else {
      base = look;
      delete overrides[symbol];
    }
    try {
      await saveChartPrefs({ look: base, overrides, templates, drag, confirmDrag });
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setLook(DEFAULT_CHART_LOOK);
    setDrag(DEFAULT_CHART_PREFS.drag);
    setConfirmDrag(DEFAULT_CHART_PREFS.confirmDrag);
  };

  const others = Object.keys(prefs.overrides).filter((s) => s !== symbol).length;

  const body = (
    <>
      {tab === 'lines' && (
        <>
          <div className="flex flex-col gap-1.5">
            <div className={sectionLabel}>
              Lines on chart <span className="font-normal tracking-normal normal-case">✎ to style</span>
            </div>
            {CHART_LINE_KEYS.map((k) => (
              <LineRow key={k} k={k} l={look.lines[k]} open={openLine === k} onOpen={() => setOpenLine(openLine === k ? null : k)} set={(p) => setLine(k, p)} />
            ))}
          </div>
          <div className="flex flex-col gap-1.5">
            <div className={sectionLabel}>Position P&amp;L on line</div>
            <Seg label="Position P&L on line" value={look.pnl} onChange={(pnl) => patch({ pnl })} options={[{ v: 'usd', label: 'USD' }, { v: 'pct', label: '% of margin' }, { v: 'off', label: 'Off' }]} />
          </div>
          {!sheet && (
            <div className="flex flex-col gap-2">
              <div className={sectionLabel}>Trading on chart</div>
              <Toggle title="Drag lines to change them" sub="Drag TP, SL or a pending order to a new price. Drag a position's line to add a TP or SL." on={drag} onChange={setDrag} />
              {drag && <Toggle title="Confirm before sending" sub="Shows the new price and P&L before the change is sent." on={confirmDrag} onChange={setConfirmDrag} />}
            </div>
          )}
        </>
      )}
      {tab === 'chart' && (
        <>
          <div className="flex flex-col gap-1.5">
            <div className={sectionLabel}>Chart type</div>
            <div className="grid grid-cols-3 gap-1">
              {(
                [
                  ['candles', 'Candles'],
                  ['hollow', 'Hollow'],
                  ['heikinAshi', 'Heikin-Ashi'],
                  ['bars', 'Bars'],
                  ['line', 'Line'],
                  ['area', 'Area'],
                ] as const
              ).map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  aria-pressed={look.type === v}
                  onClick={() => patch({ type: v })}
                  className={cx('h-7 rounded-md border text-[11.5px] font-semibold', look.type === v ? 'border-accent bg-accent-bg text-accent-fg' : 'border-line text-muted hover:text-fg')}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <div className={sectionLabel}>Rising candles</div>
            <Swatches value={look.upColor} onChange={(upColor) => patch({ upColor })} label="Rising candle colour" />
          </div>
          <div className="flex flex-col gap-1.5">
            <div className={sectionLabel}>Falling candles</div>
            <Swatches value={look.downColor} onChange={(downColor) => patch({ downColor })} label="Falling candle colour" />
          </div>
          <div className="flex flex-col gap-2">
            <div className={sectionLabel}>Show</div>
            <Toggle title="Grid lines" on={look.grid} onChange={(grid) => patch({ grid })} />
            {!sheet && <Toggle title="Volume bars" on={look.volume} onChange={(volume) => patch({ volume })} />}
            {!sheet && <Toggle title="OHLC legend" sub="Open, high, low and close in the toolbar." on={look.legend} onChange={(legend) => patch({ legend })} />}
          </div>
        </>
      )}
      {tab === 'scale' && (
        <>
          <div className="flex flex-col gap-2">
            <div className={sectionLabel}>Price axis</div>
            <Toggle title="Price tags on axis" sub="A coloured price tag for each line." on={look.axisLabels} onChange={(axisLabels) => patch({ axisLabels })} />
            <Toggle title="Candle-close countdown" sub="Time left in the current candle, by the last price." on={look.countdown} onChange={(countdown) => patch({ countdown })} />
          </div>
          <div className="flex flex-col gap-1.5">
            <div className={sectionLabel}>Scale</div>
            <Seg label="Price scale" value={look.scale} onChange={(scale) => patch({ scale })} options={[{ v: 'normal', label: 'Normal' }, { v: 'log', label: 'Log' }, { v: 'percent', label: '%' }]} />
          </div>
          <div className="flex flex-col gap-1.5">
            <div className={sectionLabel}>Time zone</div>
            <Seg label="Time zone" value={look.timezone} onChange={(timezone) => patch({ timezone })} options={[{ v: 'utc', label: 'UTC' }, { v: 'local', label: 'My time zone' }]} />
          </div>
        </>
      )}
      {tab === 'templates' && <Templates prefs={templates} look={look} onApply={setLook} onChange={setTemplates} />}

      <div className="flex flex-col gap-1.5">
        <div className={sectionLabel}>Apply to</div>
        <Seg label="Apply to" value={scope} onChange={setScope} options={[{ v: 'all', label: 'All instruments' }, { v: 'symbol', label: `${pairLabel(symbol)} only` }]} />
        {scope === 'all' && others > 0 && <p className="m-0 text-[11.5px] text-muted">{others === 1 ? '1 other instrument keeps' : `${others} other instruments keep`} its own settings.</p>}
      </div>
      {err && (
        <p role="alert" className="m-0 text-[12px] text-down">
          {err}
        </p>
      )}
    </>
  );

  const header = (
    <>
      <div className="flex items-center justify-between px-3.5 pt-3 pb-2.5">
        <h2 className="m-0 text-[13.5px] font-semibold">Chart settings</h2>
        <button type="button" onClick={onClose} aria-label="Close chart settings" className="grid size-[26px] place-items-center rounded-[7px] bg-raised text-fg">
          <Icon name="close" size={12} />
        </button>
      </div>
      <div className="px-3.5">
        <Seg
          label="Settings section"
          value={tab}
          onChange={setTab}
          options={[
            { v: 'lines', label: 'Lines' },
            { v: 'chart', label: 'Chart' },
            { v: 'scale', label: 'Scale' },
            { v: 'templates', label: 'Templates' },
          ]}
        />
      </div>
    </>
  );

  const footer = (
    <div className={cx('flex items-center gap-2 border-t border-line bg-bg px-3.5 py-2.5', sheet ? 'pb-[max(10px,env(safe-area-inset-bottom))]' : 'rounded-b-xl')}>
      <button type="button" onClick={reset} className="mr-auto text-[12px] font-medium text-muted hover:text-fg">
        Reset to defaults
      </button>
      <button type="button" onClick={onClose} className="h-8 rounded-lg border border-line-strong bg-panel px-3.5 text-[12.5px] font-semibold">
        Cancel
      </button>
      <button type="button" onClick={() => void save()} disabled={saving} className="h-8 min-w-[64px] rounded-lg bg-accent px-3.5 text-[12.5px] font-semibold text-white disabled:opacity-60">
        {saving ? <Spinner /> : 'Save'}
      </button>
    </div>
  );

  if (sheet) {
    return (
      <div className="fixed inset-0 z-[60] flex items-end justify-center bg-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
        <div ref={ref} role="dialog" aria-modal="true" aria-label="Chart settings" className="sheet-in flex max-h-[85dvh] w-full max-w-[480px] flex-col rounded-t-[20px] bg-panel text-[12px] text-fg shadow-pop">
          {header}
          <div className="flex min-h-0 flex-col gap-3.5 overflow-y-auto px-3.5 py-3">{body}</div>
          {footer}
        </div>
      </div>
    );
  }
  return (
    <div ref={ref} id="chart-settings" role="dialog" aria-label="Chart settings" className="absolute right-3 bottom-2 z-40 flex max-h-[min(640px,calc(100dvh-240px))] min-h-[320px] w-[352px] flex-col rounded-xl border border-line-strong bg-panel text-[12px] shadow-[var(--float-shadow)]">
      {header}
      <div className="flex min-h-0 flex-col gap-3.5 overflow-y-auto px-3.5 py-3">{body}</div>
      {footer}
    </div>
  );
}

/** Desktop gear that opens the chart settings popover; it sits in the account bar under the chart. */
export function ChartSettingsButton() {
  const open = useChartSettingsOpen();
  return (
    <button
      type="button"
      onClick={() => setChartSettingsOpen(!open)}
      aria-label="Chart settings"
      aria-expanded={open}
      aria-controls="chart-settings"
      className={cx('grid size-7 place-items-center rounded-full border', open ? 'border-accent bg-accent-bg text-accent-fg' : 'border-line-strong text-muted hover:border-accent hover:text-fg')}
    >
      <Icon name="settings" size={14} />
    </button>
  );
}
